// CareLogix™ ORACLE — AGENT SWARM ACTIVITY PANEL™ (Phase 33 §6)
// Live-feel animation of 10 specialized agents cycling through states.
import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { PALETTE } from "@/components/animation/MotionShared";

const AGENTS = [
  { id: "research",   label: "Research Agent",   icon: "🔍" },
  { id: "market",     label: "Market Agent",     icon: "📈" },
  { id: "risk",       label: "Risk Agent",       icon: "⚠" },
  { id: "healthcare", label: "Healthcare Agent", icon: "🏥" },
  { id: "grant",      label: "Grant Agent",      icon: "🏛" },
  { id: "funding",    label: "Funding Agent",    icon: "💰" },
  { id: "africa",     label: "Africa Agent",     icon: "🌍" },
  { id: "startup",    label: "Startup Agent",    icon: "🚀" },
  { id: "sentiment",  label: "Sentiment Agent",  icon: "💬" },
  { id: "nisi",       label: "NiSi Agent",       icon: "🧠" },
];

const STATES = [
  { id: "analyzing",     label: "Analyzing…",            color: PALETTE.gold },
  { id: "consensus",     label: "Consensus Building…",   color: PALETTE.emerald },
  { id: "confidence",    label: "Confidence Calculating…", color: PALETTE.violet },
  { id: "risk_scoring",  label: "Risk Scoring…",         color: PALETTE.amber },
  { id: "completed",     label: "Completed",             color: PALETTE.greenAccent },
];

function stepFor(agentIdx, tick) {
  // deterministic shift per agent so they don't all sync
  const idx = (tick + agentIdx * 2) % STATES.length;
  return STATES[idx];
}

export default function AgentSwarmActivityPanel({ testid = "agent-swarm-activity-panel" }) {
  const [tick, setTick] = useState(0);
  useEffect(() => {
    const id = setInterval(() => setTick((t) => t + 1), 1400);
    return () => clearInterval(id);
  }, []);

  return (
    <section style={S.shell} data-testid={testid} aria-label="Agent Swarm Activity">
      <header style={S.head}>
        <div>
          <div style={S.eyebrow}>ORACLE AGENT SWARM™ · LIVE</div>
          <h3 style={S.title}>10 Specialized Agents · Working Now</h3>
        </div>
        <span style={S.livePill} data-testid={`${testid}-live`}>
          <span style={S.liveDot} aria-hidden="true" />
          LIVE
        </span>
      </header>
      <ul style={S.grid}>
        {AGENTS.map((a, i) => {
          const s = stepFor(i, tick);
          return (
            <motion.li key={a.id} style={S.row} initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.3 }} data-testid={`${testid}-agent-${a.id}`}>
              <span style={S.icon} aria-hidden="true">{a.icon}</span>
              <span style={S.agentLbl}>{a.label}</span>
              <AnimatePresence mode="wait">
                <motion.span
                  key={s.id}
                  style={{ ...S.statePill, color: s.color, borderColor: s.color }}
                  initial={{ opacity: 0, x: 6 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -6 }}
                  transition={{ duration: 0.25 }}
                  data-testid={`${testid}-agent-${a.id}-state`}
                >
                  {s.id !== "completed" && <motion.span style={{ ...S.stateDot, background: s.color }} animate={{ opacity: [1, 0.35, 1] }} transition={{ duration: 1.4, repeat: Infinity }} />}
                  {s.label}
                </motion.span>
              </AnimatePresence>
            </motion.li>
          );
        })}
      </ul>
      <footer style={S.foot}>Agents operate under Compliance Lock™ · No autonomous execution · HITL required.</footer>
    </section>
  );
}

const S = {
  shell:    { background: "linear-gradient(180deg, rgba(8,32,22,0.55), rgba(4,6,8,0.85))", border: `1px solid ${PALETTE.goldGlow}`, borderRadius: 18, padding: 14, color: PALETTE.slate3, display: "flex", flexDirection: "column", gap: 10 },
  head:     { display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 10 },
  eyebrow:  { color: PALETTE.gold, fontFamily: "'Space Mono', monospace", fontSize: 10, letterSpacing: "0.20em" },
  title:    { margin: 0, color: PALETTE.goldBright, fontSize: 14 },
  livePill: { display: "inline-flex", alignItems: "center", gap: 6, fontFamily: "'Space Mono', monospace", fontSize: 10, color: PALETTE.greenAccent, padding: "3px 8px", border: `1px solid ${PALETTE.greenAccent}`, borderRadius: 999, letterSpacing: "0.16em" },
  liveDot:  { width: 6, height: 6, borderRadius: "50%", background: PALETTE.greenAccent, boxShadow: `0 0 8px ${PALETTE.greenAccent}` },
  grid:     { margin: 0, padding: 0, listStyle: "none", display: "grid", gridTemplateColumns: "1fr", gap: 6 },
  row:      { display: "grid", gridTemplateColumns: "22px 1fr auto", alignItems: "center", gap: 8, padding: "6px 8px", background: "rgba(255,255,255,0.02)", border: "1px solid rgba(255,255,255,0.05)", borderRadius: 8 },
  icon:     { fontSize: 14, lineHeight: 1, textAlign: "center" },
  agentLbl: { color: PALETTE.slate3, fontSize: 12.5, fontWeight: 600 },
  statePill:{ display: "inline-flex", alignItems: "center", gap: 6, fontFamily: "'Space Mono', monospace", fontSize: 10, padding: "3px 8px", border: "1px solid", borderRadius: 999, letterSpacing: "0.06em" },
  stateDot: { width: 5, height: 5, borderRadius: "50%", display: "inline-block" },
  foot:     { color: PALETTE.slate5, fontFamily: "'Space Mono', monospace", fontSize: 9.5, textAlign: "right" },
};
